import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /**
   * Callback function to trigger when the sentinel element intersects
   */
  onLoadMore: () => void;
  /**
   * Whether to enable the observer (e.g., set to false when loading or at end of list)
   */
  enabled?: boolean;
  /**
   * Root margin for the intersection observer
   * Positive values trigger earlier (before reaching the element)
   * @default '200px'
   */
  rootMargin?: string;
  /**
   * Threshold for intersection (0-1)
   * @default 0.1
   */
  threshold?: number;
}

/**
 * Custom hook for infinite scroll functionality using IntersectionObserver
 *
 * @example
 * const sentinelRef = useInfiniteScroll({
 *   onLoadMore: loadMoreItems,
 *   enabled: hasMore && !isLoading,
 * });
 *
 * return (
 *   <>
 *     {items.map(item => <Item key={item.id} {...item} />)}
 *     <div ref={sentinelRef} />
 *   </>
 * );
 */
export function useInfiniteScroll({
  onLoadMore,
  enabled = true,
  rootMargin = '200px',
  threshold = 0.1,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;

      // Only trigger if enabled and element is intersecting
      if (entry.isIntersecting && enabled) {
        onLoadMore();
      }
    },
    [onLoadMore, enabled]
  );

  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported in this browser');
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    // Create observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // viewport
      rootMargin,
      threshold,
    });

    // Start observing
    observerRef.current.observe(sentinel);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleIntersection, rootMargin, threshold]);

  return sentinelRef;
}
